import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/lib/api-client';
import type { Child, User } from '@/lib/auth-types';
import { formatGermanShortDate } from '@/lib/format-german-date';

// Shape of GET /api/credits/entitlement response. Source:
// src/credits/dto/entitlement-result.dto.ts.
export interface EntitlementResult {
  hasEntitlement: boolean;
  sources: Array<{ type: 'subscription' | 'pack' | 'trial'; remaining: number }>;
  totalRemaining: number;
  subscriptionRemaining: number;
  subscriptionPeriodEnd: string | null; // ISO string over the wire
  packRemaining: number;
  trialRemaining: number;
}

// Discriminated union for the BuilderErrorBanner's 3 limit variants.
export type LimitVariantInference =
  | { variant: 'limit-trial' }
  | { variant: 'limit-subscription-month'; formattedEndDate: string }
  | { variant: 'limit-subscription-expired'; formattedEndDate: string };


/**
 * Picks the banner variant when canCreateStory=false. Ordered check:
 *  1. Active subscription with month exhausted → month variant
 *  2. Subscription ended (past period end) → expired variant
 *  3. Everything else (no sub, trial exhausted, pack-only, etc.) → trial variant
 * Only call when canCreateStory === false.
 */
export function inferLimitVariant(entitlement: EntitlementResult): LimitVariantInference {
  const now = new Date();
  const periodEnd = entitlement.subscriptionPeriodEnd;

  if (periodEnd !== null) {
    const endDate = new Date(periodEnd);
    if (endDate > now && entitlement.subscriptionRemaining === 0) {
      return {
        variant: 'limit-subscription-month',
        formattedEndDate: formatGermanShortDate(periodEnd),
      };
    }
    if (endDate <= now) {
      return {
        variant: 'limit-subscription-expired',
        formattedEndDate: formatGermanShortDate(periodEnd),
      };
    }
  }

  return { variant: 'limit-trial' };
}

interface CachedResult {
  user: User | null;
  children: Child[];
  entitlement: EntitlementResult | null;
  error: string | null;
}

interface UseEntitlementResult {
  isLoading: boolean;
  // Gate state — only valid when isLoading === false and error === null.
  emailVerified: boolean;
  hasChildren: boolean;
  canCreateStory: boolean;
  // Raw data for consumers that need it.
  user: User | null;
  children: Child[];
  entitlement: EntitlementResult | null;
  error: string | null;
}

// Module-level cache. Populated once per session (or per explicit clear).
// Cleared via clearEntitlementCache() — wired into the story-create success
// path (B1) + asset-terminal settle (B2) + quota-surface mounts (B3).
//
// cacheGeneration (Bug 2, Flag 2): an epoch bumped on every clear. A fetch
// captures the epoch at start and only writes the cache / clears inflight if the
// epoch still matches — so a fetch already in flight when a clear happens can no
// longer re-populate the cache afterwards (orphan re-cache race).
let cache: CachedResult | null = null;
let inflight: Promise<CachedResult> | null = null;
let cacheGeneration = 0;

export function clearEntitlementCache(): void {
  cache = null;
  inflight = null;
  cacheGeneration += 1;
}

async function fetchAll(): Promise<CachedResult> {
  if (cache) return cache;
  if (inflight) return inflight;

  const gen = cacheGeneration;
  inflight = (async () => {
    const [userR, childrenR, entitlementR] = await Promise.allSettled([
      apiFetch<User>('/api/users/me'),
      apiFetch<Child[]>('/api/children'),
      apiFetch<EntitlementResult>('/api/credits/entitlement'),
    ]);

    // 401 on any fetch: token expired after api-client's refresh flow failed.
    // Let the normal auth flow handle the redirect — surface no gate data.
    const has401 = [userR, childrenR, entitlementR].some(
      (r) => r.status === 'rejected' && r.reason instanceof ApiError && r.reason.httpStatus === 401,
    );
    if (has401) {
      const result: CachedResult = {
        user: null,
        children: [],
        entitlement: null,
        error: 'unauthorized',
      };
      if (gen === cacheGeneration) cache = result;
      return result;
    }

    const user = userR.status === 'fulfilled' ? userR.value : null;
    const children = childrenR.status === 'fulfilled' ? childrenR.value : [];
    const entitlement = entitlementR.status === 'fulfilled' ? entitlementR.value : null;
    const anyFailed = [userR, childrenR, entitlementR].some((r) => r.status === 'rejected');
    const error = anyFailed ? 'Statusinformationen konnten nicht geladen werden.' : null;

    const result: CachedResult = { user, children, entitlement, error };
    if (gen === cacheGeneration) cache = result;
    return result;
  })().finally(() => {
    if (gen === cacheGeneration) inflight = null;
  });

  return inflight;
}

/**
 * Clear + warm the entitlement cache (Bug 2, B2). Best-effort module-level
 * refresh used after a story's assets settle (deduction + any refund committed),
 * so the next quota-surface mount reads the correct remaining.
 */
export async function refetchEntitlementCache(): Promise<void> {
  clearEntitlementCache();
  await fetchAll();
}

export function useEntitlement(options?: { refetchOnMount?: boolean }): UseEntitlementResult {
  const refetchOnMount = options?.refetchOnMount ?? false;
  const [state, setState] = useState<CachedResult | null>(cache);

  useEffect(() => {
    // B3 (Bug 2): force a fresh fetch on mount for quota surfaces (Start /
    // Account) so "Noch X von Y" + plan label never render a stale cache.
    if (refetchOnMount) clearEntitlementCache();
    if (cache) return; // initial state already seeded from cache
    let cancelled = false;
    fetchAll()
      .then((result) => {
        if (!cancelled) setState(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          user: null,
          children: [],
          entitlement: null,
          error:
            err instanceof Error
              ? err.message
              : 'Statusinformationen konnten nicht geladen werden.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [refetchOnMount]);

  const isLoading = state === null;

  // Err on the side of "block" when data is missing — prevents the form
  // flashing open before gates evaluate.
  return {
    isLoading,
    emailVerified: state?.user?.emailVerified ?? false,
    hasChildren: (state?.children.length ?? 0) > 0,
    canCreateStory: state?.entitlement?.hasEntitlement ?? false,
    user: state?.user ?? null,
    children: state?.children ?? [],
    entitlement: state?.entitlement ?? null,
    error: state?.error ?? null,
  };
}
